import React, { useState, useEffect, useCallback } from 'react';
import { Globe, ChevronDown, ChevronUp } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { apiClient } from '@/utils/apiClient';
import { useTranslation } from '@/i18n';
import { clsx } from 'clsx';
import {
  reconcileClusterSelection,
  setKnownClusterIds,
  setKnownClusters,
} from '@/utils/clusterReconcile';

interface ClusterInfo {
  id: string;
  name: string;
  base_url: string;
}

interface ClusterModeData {
  preference: {
    mode: string;
    synthesis_mode: string;
    selected_cluster_ids: string[];
  };
  multi_cluster_available: boolean;
  cluster_count: number;
  is_cluster_bound: boolean;
  clusters: ClusterInfo[];
}

/**
 * Inline cluster mode selector — only renders when >1 active clusters are
 * configured and the deployment is not cluster-bound. Shows a collapsed
 * pill above the chat input that expands to reveal mode options and
 * cluster checkboxes.
 */
export const ClusterModeSelector: React.FC = () => {
  const { settings, updateSettings } = useAppStore();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [data, setData] = useState<ClusterModeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);

  // STORY-256 / BUG-1.4.0_2: reconcile the persisted selection against the LIVE
  // cluster list on every fetch — not just first load — so a stale selection
  // (cluster removed/re-added/re-seeded, or changed in another tab) never
  // reaches the wire, where the backend would drop the unknown id and collapse
  // the fan-out to the default cluster. This effect runs even when the selector
  // renders null (availability dropped to 1), because hooks run before the
  // early return below — that is exactly the case a mount-only reconcile missed.
  const fetchData = useCallback(async () => {
    try {
      const result = await apiClient.getClusterMode();
      setData(result);
      const liveIds = (result.clusters || []).map((c) => c.id);
      setKnownClusterIds(liveIds);  // cache for the send-time guard in useChatService
      // STORY-300: names too, for the chat input's @-mention suggestions.
      setKnownClusters(result.clusters || [], Boolean(result.multi_cluster_available));

      const st = useAppStore.getState().settings;
      if (!st.clusterMode) {
        // First load: adopt the server preference (backend already prunes it).
        updateSettings({
          clusterMode: result.preference.mode as any,
          selectedClusterIds: result.preference.selected_cluster_ids,
          synthesisModeOnMulti: result.preference.synthesis_mode as any,
        });
        return;
      }
      // Subsequent loads/focus: prune stale ids + downgrade a collapsed fan-out.
      const r = reconcileClusterSelection(
        st.selectedClusterIds, liveIds, st.clusterMode as any);
      if (r.changed) {
        updateSettings({
          selectedClusterIds: r.selectedClusterIds,
          clusterMode: r.clusterMode,
        });
        // Persist the reconciled preference (best-effort).
        apiClient.setClusterMode({
          mode: r.clusterMode,
          selected_cluster_ids: r.selectedClusterIds,
        }).catch(() => { /* best-effort */ });
        const droppedNames = r.dropped
          .map((id) => (result.clusters.find((c) => c.id === id)?.name) || id)
          .join(', ');
        setNotice(
          (t.clusterMode?.selectionUpdated ||
            'Cluster selection updated — no longer available: ') + droppedNames,
        );
      }
    } catch {
      // Endpoint missing or auth failed — hide selector
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Re-reconcile when the user returns to the tab — clusters may have changed
    // in another session/tab while this one held a stale selection.
    window.addEventListener('focus', fetchData);
    return () => window.removeEventListener('focus', fetchData);
  }, [fetchData]);

  // Don't render if: loading, unavailable, or only 1 cluster
  if (loading || !data?.multi_cluster_available) return null;

  const clusters = data.clusters;
  const currentMode = settings.clusterMode || 'single';
  const selectedIds = settings.selectedClusterIds || [];
  const synthesis = settings.synthesisModeOnMulti || 'synthesise';

  const modes = [
    { key: 'single', label: t.clusterMode?.single || 'One cluster', desc: t.clusterMode?.singleDesc || 'Fastest. Query a single cluster.' },
    { key: 'sequential', label: t.clusterMode?.sequential || 'All — sequential', desc: t.clusterMode?.sequentialDesc || 'Query each cluster in turn.' },
    { key: 'parallel', label: t.clusterMode?.parallel || 'All — parallel', desc: t.clusterMode?.parallelDesc || 'Query all clusters at once.' },
  ];

  // BUG-1.4.1_1 (UI): entering One-cluster mode must leave exactly one id in
  // the selection — the send path only routes single-cluster when the
  // selection holds one id, so a multi-id leftover from a fan-out mode would
  // silently fall back to the default cluster.
  const handleModeChange = async (mode: string) => {
    const patch: { mode: string; selected_cluster_ids?: string[] } = { mode };
    if (mode === 'single' && selectedIds.length > 1) {
      patch.selected_cluster_ids = [selectedIds[0]];
      updateSettings({ clusterMode: mode as any, selectedClusterIds: patch.selected_cluster_ids });
    } else {
      updateSettings({ clusterMode: mode as any });
    }
    try { await apiClient.setClusterMode(patch); } catch { /* best-effort persist */ }
  };

  // BUG-1.4.1_1 (UI): direct active-cluster pick for One-cluster mode — the
  // panel previously offered no way to choose which cluster is active
  // (checkboxes rendered only for fan-out modes), forcing the
  // sequential-uncheck workaround. Replaces the whole selection with the one
  // picked id so the request travels as cluster_mode 'single' + that id.
  const handleActiveClusterSelect = async (clusterId: string) => {
    updateSettings({ selectedClusterIds: [clusterId] });
    try { await apiClient.setClusterMode({ selected_cluster_ids: [clusterId] }); } catch { /* best-effort persist */ }
  };

  const handleClusterToggle = async (clusterId: string) => {
    const next = selectedIds.includes(clusterId)
      ? selectedIds.filter(id => id !== clusterId)
      : [...selectedIds, clusterId];
    updateSettings({ selectedClusterIds: next });
    try { await apiClient.setClusterMode({ selected_cluster_ids: next }); } catch {}
  };

  const handleSynthesisToggle = async () => {
    const next = synthesis === 'synthesise' ? 'concat' : 'synthesise';
    updateSettings({ synthesisModeOnMulti: next });
    try { await apiClient.setClusterMode({ synthesis_mode: next }); } catch {}
  };

  // Active cluster in single mode: only an exact one-id selection routes — an
  // empty (or stale multi-id) selection sends no cluster fields and the
  // backend uses its default cluster.
  const activeSingleId = selectedIds.length === 1 ? selectedIds[0] : null;
  const activeSingleName = activeSingleId
    ? (clusters.find(c => c.id === activeSingleId)?.name || activeSingleId)
    : (t.clusterMode?.defaultClusterShort || 'default');

  const modeShortLabel = currentMode === 'parallel'
    ? (t.clusterMode?.parallelShort || 'parallel')
    : (t.clusterMode?.sequentialShort || 'sequential');
  const pillLabel = currentMode === 'single'
    ? `${t.clusterMode?.pill || 'Clusters'}: ${activeSingleName}`
    : `${t.clusterMode?.pill || 'Clusters'}: ${selectedIds.length}/${clusters.length} (${modeShortLabel})`;

  return (
    <div className="mb-2">
      {/* Collapsed pill */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={clsx(
          'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
          'border border-gray-300 dark:border-gray-600',
          'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200',
          'hover:bg-gray-100 dark:hover:bg-gray-700',
        )}
      >
        <Globe className="h-3.5 w-3.5" />
        <span>{pillLabel}</span>
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>

      {/* STORY-256: non-blocking notice when a stale selection was reconciled */}
      {notice && (
        <div className="mt-1 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
          <span className="flex-1">{notice}</span>
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="text-amber-600 dark:text-amber-500 hover:underline"
          >
            {t.common?.dismiss || 'Dismiss'}
          </button>
        </div>
      )}

      {/* Expanded panel */}
      {expanded && (
        <div className="mt-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm space-y-3">
          {/* Mode selector */}
          <div className="space-y-1">
            {modes.map(m => (
              <label
                key={m.key}
                className={clsx(
                  'flex items-start gap-2 p-1.5 rounded cursor-pointer',
                  currentMode === m.key
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-750',
                )}
              >
                <input
                  type="radio"
                  name="cluster-mode"
                  checked={currentMode === m.key}
                  onChange={() => handleModeChange(m.key)}
                  className="mt-0.5"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{m.label}</span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{m.desc}</p>
                </div>
              </label>
            ))}
          </div>

          {/* Active-cluster radios — single mode (BUG-1.4.1_1 UI fix) */}
          {currentMode === 'single' && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {t.clusterMode?.activeCluster || 'Active cluster:'}
              </p>
              <div className="space-y-1">
                {clusters.map(c => (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="active-cluster"
                      checked={activeSingleId === c.id}
                      onChange={() => handleActiveClusterSelect(c.id)}
                    />
                    <span className="text-gray-800 dark:text-gray-200">{c.name}</span>
                    <span className="text-xs text-gray-400 truncate max-w-[200px]">{c.base_url}</span>
                  </label>
                ))}
              </div>
              {activeSingleId === null && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {t.clusterMode?.noActiveClusterHint ||
                    'No cluster selected — the default cluster will be used.'}
                </p>
              )}
            </div>
          )}

          {/* Cluster checkboxes — only for non-single modes */}
          {currentMode !== 'single' && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t.clusterMode?.selectClusters || 'Select clusters:'}
                </p>
                <div className="space-y-1">
                  {clusters.map(c => (
                    <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(c.id)}
                        onChange={() => handleClusterToggle(c.id)}
                      />
                      <span className="text-gray-800 dark:text-gray-200">{c.name}</span>
                      <span className="text-xs text-gray-400 truncate max-w-[200px]">{c.base_url}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Synthesis toggle */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={synthesis === 'synthesise'}
                    onChange={handleSynthesisToggle}
                  />
                  <div>
                    <span className="text-gray-800 dark:text-gray-200">
                      {t.clusterMode?.synthesis || 'Merge answers with a final summary'}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {synthesis === 'synthesise'
                        ? (t.clusterMode?.synthesisOnDesc || 'One extra LLM call to merge cluster answers.')
                        : (t.clusterMode?.synthesisOffDesc || 'Show each cluster\'s answer separately (faster).')}
                    </p>
                  </div>
                </label>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
